import {find, map} from 'lodash';
import {useMemo, useEffect, useState, useCallback} from 'react';
import {useTranslation} from 'react-i18next';

import {userSettingsApi} from 'api';
import {Dropdown, DropdownItem, Select} from 'components';
import {usePrevious, useApiData} from 'hooks';
import {Option} from 'interfaces/option';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {LanguageService} from 'services/Language';
import {userAuthUtils, languageUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.LANGUAGE_SWITCHER;

type Props = {
    variant?: 'select' | 'dropdown'
    className?: string;
    dropdownOpen?: boolean;
    icon?: boolean;
    hidePlaceholder?: boolean;
    onDropdownClose?: () => void;
};

export const LanguageSwitcher: React.FC<Props> = (
    {className, variant = 'select', dropdownOpen: dropdownOpenProp, icon, hidePlaceholder, onDropdownClose}: Props) => {
    const {t} = useTranslation();
    const [selectedLanguageId, setSelectedLanguageId] = useState<string>();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const prevOpenDropdownProp = usePrevious(dropdownOpenProp);
    const options = useMemo<Option[]>(() => languageUtils.getLanguageOptions(t), [t]);

    const selectedOption = useMemo<Option | null>(() => find(options, ({id}) => selectedLanguageId === id), [selectedLanguageId, options]);
    const {send: updateLanguageSettings} = useApiData(userSettingsApi.updateLanguageSettings);

    const onChangeLanguage = useCallback((value) => {
        setSelectedLanguageId(value.id);
        if (userAuthUtils.isLoggedIn()) {
            updateLanguageSettings({value: value.id});
        }
        LanguageService.setLanguage(value.id);
    }, [updateLanguageSettings]);

    useEffect(() => {
        if (!selectedLanguageId) {
            const found = find(options, ({id}) => id === LanguageService.getLanguage());
            if (found) {
                setSelectedLanguageId(found.id);
            }
        }
    }, [options, selectedLanguageId]);

    useEffect(() => {
        if (!prevOpenDropdownProp && dropdownOpenProp) {
            setDropdownOpen(true);
        }
    }, [dropdownOpenProp, prevOpenDropdownProp]);

    const openDropdown = useCallback(() => {
        setDropdownOpen(true);
    }, []);

    const closeDropdown = useCallback(() => {
        onDropdownClose?.();
        setDropdownOpen(false);
    }, [onDropdownClose]);

    useEffect(() => LanguageService.subscribeLanguageChange(setSelectedLanguageId), []);

    if (variant === 'select') {
        return (<Select
            placeholder={t(T_KEYS.TITLE)}
            hidePlaceholder={hidePlaceholder}
            multiple={false}
            options={options}
            className={className}
            value={selectedOption}
            onChange={onChangeLanguage}
        />);
    }

    return (
        <Dropdown
            open={dropdownOpen}
            controlled
            style={{display: 'block'}}
            childrenClassName={className}
            icon={icon}
            triggerNode={
                <div className='tw-flex tw-items-center tw-cursor-pointer'>
                    <img className='tw-w-5 tw-mr-3 tw-h-full' src={selectedOption?.icon} alt={selectedOption?.name} />
                    <div>{selectedOption?.name}</div>
                </div>
            }
            onOpen={openDropdown}
            onClose={closeDropdown}
        >
            {map(options, (option) => (
                <DropdownItem key={option.id} selected={option.id === selectedOption?.id} onClick={() => onChangeLanguage(option)}>
                    <div className='tw-flex tw-items-center tw-cursor-pointer'>
                        <img className='tw-w-5 tw-mr-3 tw-h-full' src={option.icon} alt={option.name} />
                        <div>{option.name}</div>
                    </div>
                </DropdownItem>
            ))}
        </Dropdown>
    );
};